import React, { createContext, useContext, useEffect, useState } from "react";
import { Company } from "../types";
import {
  getThemePalette,
  generateThemeCSS,
  ThemePalette,
} from "../utils/themeUtils";

interface ThemeContextType {
  company: Company | null;
  theme: ThemePalette;
  setCompany: (company: Company | null) => void;
  getThemeColor: (colorPath: string) => string;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

interface ThemeProviderProps {
  children: React.ReactNode;
  initialCompany?: Company | null;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({
  children,
  initialCompany = null,
}) => {
  const [company, setCompany] = useState<Company | null>(initialCompany);
  const theme = getThemePalette(company);

  // Function to get nested color values
  const getThemeColor = (colorPath: string): string => {
    const pathParts = colorPath.split(".");
    let color: any = theme;

    for (const part of pathParts) {
      if (color && typeof color === "object" && part in color) {
        color = color[part];
      } else {
        return ""; // Return empty string if path doesn't exist
      }
    }

    return typeof color === "string" ? color : "";
  };

  // Update CSS custom properties when company changes
  useEffect(() => {
    if (typeof document !== "undefined") {
      const styleElement = document.getElementById("dynamic-theme-styles");
      if (styleElement) {
        styleElement.remove();
      }

      const newStyleElement = document.createElement("style");
      newStyleElement.id = "dynamic-theme-styles";
      newStyleElement.textContent = generateThemeCSS(company);
      document.head.appendChild(newStyleElement);
    }
  }, [company]);

  const value: ThemeContextType = {
    company,
    theme,
    setCompany,
    getThemeColor,
  };

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
};

export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
};
